<script setup lang="ts">
import { watch } from 'vue';
import { useCounter } from '@vueuse/core';
import { consola } from 'consola';

const props = defineProps({
  price: {
    type: Number,
    required: false,
  },
  size: {
    type: String,
    required: true,
    default: 'md',
  },
  countV: {
    type: Number,
    required: true,
    default: 1,
  },
  minV: {
    type: Number,
    required: true,
    default: 0,
  },
  maxV: {
    type: Number,
    required: true,
    default: 100,
  },
  step: {
    type: Number,
    required: false,
    default: 1,
  },
});

const emit = defineEmits<{
  (e: 'count', value: number): void;
}>();

const logger = consola.withTag('BaseCounter');

function safeNumber(val: any, fallback: number): number {
  const n = Number(val);
  return Number.isNaN(n) ? fallback : n;
}

// Нормализация значения к шагу (избегаем проблем с float)
// ✅ ВАЖНО: Функция должна быть определена ДО использования
function normalizeToStep(value: number): number {
  const step = props.step ?? 1; // Используем step из props
  if (step === 1) return value;
  // Округляем к ближайшему кратному шага
  const rounded = Math.round(value / step) * step;
  // Округляем до 2 знаков после запятой для шага 0.25
  return Math.round(rounded * 100) / 100;
}

const initialCount = safeNumber(props.countV, props.minV);

logger.debug('🔢 BaseCounter initialized', {
  initialCount,
  countV: props.countV,
  minV: props.minV,
  maxV: props.maxV,
  step: props.step,
  price: props.price,
});

// ✅ ВАЖНО: Нормализуем начальное значение к шагу
const normalizedInitialCount = normalizeToStep(initialCount);
const { count, inc, dec, set } = useCounter(normalizedInitialCount, { min: props.minV, max: props.maxV });

// ✅ КРИТИЧНО: Устанавливаем нормализованное значение сразу после инициализации
if (normalizedInitialCount !== initialCount) {
  set(normalizedInitialCount);
  logger.debug('🔧 Normalized initial count', {
    original: initialCount,
    normalized: normalizedInitialCount,
    step: props.step,
  });
}

// Форматирование для отображения (убираем лишние нули)
function formatCount(value: number): string {
  if (props.step === 1) return String(value);
  // Для дробных шагов показываем до 2 знаков, убираем лишние нули
  return value % 1 === 0 ? String(value) : value.toFixed(2).replace(/\.?0+$/, '');
}

// Безопасный инкремент/декремент с контролем диапазона (не уйдём за min/max)
function safeInc() {
  const currentValue = normalizeToStep(count.value);
  const stepToUse = props.step ?? 1; // ✅ КРИТИЧНО: Используем step из props
  const nextValue = normalizeToStep(currentValue + stepToUse);

  logger.debug('➕ safeInc called', {
    currentCount: count.value,
    currentValue,
    nextValue,
    step: stepToUse,
    propsStep: props.step,
    maxV: props.maxV,
    canIncrement: nextValue <= props.maxV,
    calculation: `${currentValue} + ${stepToUse} = ${currentValue + stepToUse} → normalized: ${nextValue}`,
  });

  if (nextValue <= props.maxV) {
    inc(stepToUse); // ✅ Используем stepToUse
    // Нормализуем значение после инкремента
    const normalized = normalizeToStep(count.value);
    if (normalized !== count.value) {
      set(normalized);
    }
    logger.debug('✅ Incremented', {
      newCount: count.value,
      normalized,
      stepUsed: stepToUse,
      expected: currentValue + stepToUse,
    });
  } else {
    logger.warn('⚠️ Cannot increment: reached max', { count: count.value, max: props.maxV });
  }
}

function safeDec() {
  const currentValue = normalizeToStep(count.value);
  const stepToUse = props.step ?? 1; // ✅ КРИТИЧНО: Используем step из props
  const nextValue = normalizeToStep(currentValue - stepToUse);

  logger.debug('➖ safeDec called', {
    currentCount: count.value,
    currentValue,
    nextValue,
    step: stepToUse,
    propsStep: props.step,
    minV: props.minV,
    canDecrement: nextValue >= props.minV,
    calculation: `${currentValue} - ${stepToUse} = ${currentValue - stepToUse} → normalized: ${nextValue}`,
  });

  if (nextValue >= props.minV) {
    dec(stepToUse); // ✅ Используем stepToUse
    // Нормализуем значение после декремента
    const normalized = normalizeToStep(count.value);
    if (normalized !== count.value) {
      set(normalized);
    }
    logger.debug('✅ Decremented', {
      newCount: count.value,
      normalized,
      stepUsed: stepToUse,
      expected: currentValue - stepToUse,
    });
  } else if (currentValue <= props.minV) {
    logger.debug('⚠️ At minimum, emitting 0');
    emit('count', 0); // явно эмитим 0 даже если декремент невозможен
  }
}

// Слежение за count и эмит наружу только валидных чисел
watch(count, (newValue) => {
  // Нормализуем значение к шагу перед эмитом
  const normalized = normalizeToStep(newValue);
  const safeValue = safeNumber(normalized, props.minV);
  logger.info('📤 Emitting count event', {
    newValue,
    normalized,
    safeValue,
    countV: props.countV,
    step: props.step,
  });
  emit('count', safeValue);
});

// Слежение за входящим props.countV — синхронизация
watch(
  () => props.countV,
  (newValue) => {
    const safeValue = safeNumber(newValue, props.minV);
    // Нормализуем к шагу при синхронизации
    const normalized = normalizeToStep(safeValue);
    logger.debug('🔄 countV prop changed, syncing', {
      newValue,
      safeValue,
      normalized,
      currentCount: count.value,
      step: props.step,
      minV: props.minV,
      maxV: props.maxV,
    });
    set(normalized);
  },
);

// ✅ ВАЖНО: Следим за изменением step и minV, чтобы пересчитать текущее значение
watch(
  () => [props.step, props.minV],
  ([newStep, newMinV]) => {
    logger.debug('🔄 step or minV changed, renormalizing', {
      newStep,
      newMinV,
      currentCount: count.value,
      oldStep: props.step,
      oldMinV: props.minV,
    });
    // Пересчитываем текущее значение с новым шагом
    const normalized = normalizeToStep(count.value);
    if (normalized !== count.value) {
      set(normalized);
    }
  },
);
</script>

<template>
  <template v-if="props.size === 'md'">
    <div class="counter flex justify-between">
      <UiButtonMain
        class="dark:bg-transparent"
        size="lg"
        icon="i-heroicons-minus-16-solid"
        variant="soft"
        label=""
        @click.stop="safeDec()"
      />

      <!-- И цена, и количество перелистываются одним и тем же компонентом:
           движение в карточке товара должно быть одно, а не две похожие
           анимации из разных мест. Цена меняется от добавок, количество —
           от кнопок рядом; направление каждая считает по своему прошлому
           значению (см. UiRollingValue). -->
      <div class="counter__input">
        <template v-if="props.price">
          <UiRollingValue :value="props.price" suffix="₽" class="currency-symbol" />
          <span class="currency-symbol">&nbsp;*&nbsp;</span>
        </template>
        <UiRollingValue v-slot="{ value }" :value="count" class="currency-symbol">
          {{ formatCount(value) }}
        </UiRollingValue>
      </div>

      <UiButtonMain
        class="dark:bg-transparent"
        size="lg"
        icon="i-heroicons-plus-16-solid"
        variant="soft"
        label=""
        @click.stop="safeInc()"
      />
    </div>
  </template>
  <template v-else-if="props.size === 'sm'">
    <div class="counter counter-sm flex justify-between">
      <UiButtonMain
        class="dark:bg-transparent"
        size="2xs"
        rounded="rounded-full"
        icon="i-heroicons-minus-16-solid"
        variant="soft"
        label=""
        @click.stop="safeDec()"
      />

      <!-- И цена, и количество перелистываются одним и тем же компонентом:
           движение в карточке товара должно быть одно, а не две похожие
           анимации из разных мест. Цена меняется от добавок, количество —
           от кнопок рядом; направление каждая считает по своему прошлому
           значению (см. UiRollingValue). -->
      <div class="counter__input">
        <template v-if="props.price">
          <UiRollingValue :value="props.price" suffix="₽" class="currency-symbol" />
          <span class="currency-symbol">&nbsp;*&nbsp;</span>
        </template>
        <UiRollingValue v-slot="{ value }" :value="count" class="currency-symbol">
          {{ formatCount(value) }}
        </UiRollingValue>
      </div>

      <UiButtonMain
        class="dark:bg-transparent"
        size="2xs"
        rounded="rounded-full"
        icon="i-heroicons-plus-16-solid"
        variant="soft"
        label=""
        @click.stop="safeInc()"
      />
    </div>
  </template>
</template>

<style scoped lang="scss">
.counter {
  @apply border border-zinc-300 dark:border-neutral-900 bg-zinc-200 text-white dark:text-white dark:bg-zinc-800 p-2 flex gap-4 items-center;
  // Скругление из пресета «Скругления элементов» (раздел «Внешний вид»).
  // Цвет счётчика остаётся нейтральным: «плюс» и «минус» — это шаг количества,
  // а не главное действие, и красить их в фирменный цвет значит спорить с кнопкой «в корзину».
  border-radius: var(--app-radius-control, 0.5rem);

  &-sm {
    border-radius: var(--app-radius-control, 9999px);
  }

  &__input {
    @apply w-28 relative flex justify-center items-center;
    // Число уезжает за границы блока во время перелистывания — прячем «хвосты»
    overflow: hidden;
    // Цена и количество — две отдельные ячейки, между ними не должно быть переноса
    white-space: nowrap;

    input {
      @apply w-11;
    }

    .currency-symbol {
      @apply text-black dark:text-white;
    }
  }
}

// Кадры перелистывания живут в UiRollingValue — здесь их держать незачем,
// тем же движением меняется цена в кнопке карточки товара.

.counter-sm {
  @apply gap-2;
}
</style>
